/**
 * @file ErrorBoundary.jsx
 * @description Global error boundary — catches unhandled React render errors
 *              and displays a user-friendly fallback UI with a reload option.
 *              Must be a class component (React limitation for error boundaries).
 */
import React from 'react';

class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Error Boundary Caught:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="min-h-screen bg-industrial-900 flex items-center justify-center p-6">
                    <div className="bg-industrial-800 border border-industrial-700 rounded-xl p-8 max-w-md">
                        <div className="text-action-danger text-4xl mb-4">⚠️</div>
                        <h1 className="text-2xl font-bold text-industrial-100 mb-2">
                            Something went wrong
                        </h1>
                        <p className="text-industrial-500 mb-4">
                            The application encountered an unexpected error. Please refresh the page.
                        </p>
                        <button
                            onClick={() => window.location.reload()}
                            className="bg-action-info hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                        >
                            Reload Application
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
